import { appendFileToFormData } from "../form-data";
import type { InspectionAngleValue } from "../inspection-tier-requirements";
import api from "../api";

export interface InspectionItem {
    id?: number;
    vehicle_inspection_id?: number;
    item_name: string;
    condition: "excellent" | "good" | "fair" | "poor" | "damaged";
    remarks?: string;
    created_at?: string;
    updated_at?: string;
}

export interface InspectionMedia {
    id: number;
    vehicle_inspection_id: number;
    media_type: "photo" | "video";
    file_path: string;
    file_size: number;
    mime_type: string;
    caption?: string;
    url: string;
    created_at: string;
    updated_at: string;
}

export interface VehicleInspection {
    id: number;
    booking_id: number;
    type: "pickup" | "delivery";
    inspector_id: number;
    odometer_reading?: number;
    fuel_level: "empty" | "quarter" | "half" | "three_quarters" | "full";
    general_remarks?: string;
    inspected_at: string;
    created_at: string;
    updated_at: string;
    items: InspectionItem[];
    media: InspectionMedia[];
    inspector?: {
        id: number;
        name: string;
        email: string;
        phone: string;
    };
}

export interface CreateInspectionData {
    type: "pickup" | "delivery";
    vin?: string;
    warning_lights_present?: boolean;
    odometer_reading?: number;
    fuel_level?: "empty" | "quarter" | "half" | "three_quarters" | "full";
    general_remarks?: string;
    obd_fault_codes?: string[] | null;
    items?: {
        item_name: string;
        condition: "excellent" | "good" | "fair" | "poor" | "damaged";
        remarks?: string;
    }[];
}

export interface MediaUploadItem {
    uri: string;
    type: "photo" | "video";
    id: string;
    angle?: InspectionAngleValue;
}

export interface MediaUploadResult {
    succeeded: MediaUploadItem[];
    failed: MediaUploadItem[];
}

const UPLOAD_MAX_RETRIES = 3;
const UPLOAD_TIMEOUT_MS = 120_000;

export function buildUploadFile(
    fileUri: string,
    mediaType: "photo" | "video",
): { uri: string; name: string; type: string } {
    // blob: URLs have no filename/extension — strip query/hash fragments too
    const rawName = fileUri.split("/").pop()?.split("?")[0]?.split("#")[0] || "";
    const extension = /\.(\w+)$/.exec(rawName)?.[1]?.toLowerCase();

    if (mediaType === "video") {
        const name =
            extension && ["mp4", "mov", "avi"].includes(extension)
                ? rawName
                : `video-${Date.now()}.mp4`;
        const type = name.toLowerCase().endsWith(".mov")
            ? "video/quicktime"
            : "video/mp4";

        return { uri: fileUri, name, type };
    }

    if (extension === "png") {
        return { uri: fileUri, name: rawName || `photo-${Date.now()}.png`, type: "image/png" };
    }

    if (extension === "heic" || extension === "heif" || extension === "avif") {
        const name = rawName.replace(/\.(heic|heif|avif)$/i, ".jpg");

        return { uri: fileUri, name, type: "image/jpeg" };
    }

    const name =
        extension && ["jpg", "jpeg"].includes(extension)
            ? rawName
            : `photo-${Date.now()}.jpg`;

    return { uri: fileUri, name, type: "image/jpeg" };
}

export async function appendInspectionFile(
    formData: FormData,
    fileUri: string,
    mediaType: "photo" | "video",
): Promise<void> {
    await appendFileToFormData(formData, "file", buildUploadFile(fileUri, mediaType));
}

class InspectionService {
    /**
     * Create a new vehicle inspection (idempotent — returns existing if already created).
     */
    async createInspection(
        bookingId: number,
        data: CreateInspectionData
    ): Promise<VehicleInspection> {
        const response = await api.post(`/inspections/bookings/${bookingId}`, data);
        return response.data.data;
    }

    /**
     * Get an existing inspection for a booking by type.
     */
    async getInspectionForBooking(
        bookingId: number,
        type: "pickup" | "delivery"
    ): Promise<VehicleInspection | null> {
        try {
            const response = await api.get(`/inspections/bookings/${bookingId}`, {
                params: { type },
            });
            return response.data.data;
        } catch (error: any) {
            if (error.response?.status === 404) {
                return null;
            }
            throw error;
        }
    }

    /**
     * Get inspection details
     */
    async getInspection(inspectionId: number): Promise<VehicleInspection> {
        const response = await api.get(`/inspections/${inspectionId}`);
        return response.data.data;
    }

    /**
     * Finalize an inspection after all media has been uploaded.
     */
    async completeInspection(inspectionId: number): Promise<VehicleInspection> {
        const response = await api.post(`/inspections/${inspectionId}/complete`);
        return response.data.data;
    }

    /**
     * Upload media (photo or video) for an inspection
     */
    async uploadInspectionMedia(
        inspectionId: number,
        fileUri: string,
        mediaType: "photo" | "video",
        caption?: string
    ): Promise<InspectionMedia> {
        const formData = new FormData();

        await appendInspectionFile(formData, fileUri, mediaType);
        formData.append("media_type", mediaType);

        if (caption) {
            formData.append("caption", caption);
        }

        const response = await api.post(
            `/inspections/${inspectionId}/media`,
            formData,
            {
                timeout: mediaType === "video" ? UPLOAD_TIMEOUT_MS : 60_000,
            }
        );

        return response.data.data;
    }

    /**
     * Upload media with automatic retries per file.
     */
    async uploadInspectionMediaWithRetry(
        inspectionId: number,
        fileUri: string,
        mediaType: "photo" | "video",
        caption?: string
    ): Promise<InspectionMedia> {
        let lastError: unknown;

        for (let attempt = 1; attempt <= UPLOAD_MAX_RETRIES; attempt++) {
            try {
                return await this.uploadInspectionMedia(
                    inspectionId,
                    fileUri,
                    mediaType,
                    caption
                );
            } catch (error) {
                lastError = error;
                if (attempt < UPLOAD_MAX_RETRIES) {
                    await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
                }
            }
        }

        throw lastError;
    }

    /**
     * Upload multiple media files, tracking successes and failures.
     */
    async uploadAllMedia(
        inspectionId: number,
        mediaItems: MediaUploadItem[],
        onProgress?: (done: number, total: number) => void
    ): Promise<MediaUploadResult> {
        const succeeded: MediaUploadItem[] = [];
        const failed: MediaUploadItem[] = [];
        const total = mediaItems.length;

        for (let index = 0; index < mediaItems.length; index++) {
            const mediaItem = mediaItems[index];

            try {
                await this.uploadInspectionMediaWithRetry(
                    inspectionId,
                    mediaItem.uri,
                    mediaItem.type,
                    mediaItem.angle
                );
                succeeded.push(mediaItem);
            } catch (error) {
                console.error("Failed to upload media:", error);
                failed.push(mediaItem);
            }

            onProgress?.(index + 1, total);
        }

        return { succeeded, failed };
    }

    /**
     * Delete inspection media
     */
    async deleteInspectionMedia(mediaId: number): Promise<void> {
        await api.delete(`/inspections/media/${mediaId}`);
    }
}

export default new InspectionService();
